import * as React from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/**
 * `navigate` state for a push or replace that only changes view state on the
 * current pane (a table filter, a search keystroke): the scroller keeps its
 * position instead of resetting to the top.
 */
export const PRESERVE_SCROLL_STATE = { preserveScroll: true } as const;

export const hasPreserveScrollState = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as { preserveScroll?: unknown }).preserveScroll === true;

/** Saved positions beyond this many history entries are dropped, oldest first. */
const MAX_SAVED_POSITIONS = 50;

/** How long a restore keeps retrying while the pane is still shorter than the target. */
export const RESTORE_DEADLINE_MS = 1500;

export interface IResolveScrollTargetInput {
  navigationType: NavigationType;
  /** The new entry carries `PRESERVE_SCROLL_STATE`. */
  preserveScroll: boolean;
  /** The position saved for the new entry, if it was visited before. */
  saved: number | undefined;
  /** Where the scroller is now. */
  current: number;
}

/**
 * Where the scroller should sit after a location change: a history move
 * restores what was saved for the entry, a view-state write holds still, and
 * any other push or replace is a new page and starts at the top.
 */
export const resolveScrollTarget = ({
  navigationType,
  preserveScroll,
  saved,
  current,
}: IResolveScrollTargetInput): number => {
  if (navigationType === NavigationType.Pop) {
    return saved ?? 0;
  }
  return preserveScroll ? current : 0;
};

interface IPendingRestore {
  target: number;
  /** Where the last assignment actually landed once the browser clamped it. */
  applied: number;
  deadline: number;
  frame: number;
}

/**
 * Restores the pane scroller's position across history moves. html/body are
 * locked (see styles/app.css), so the browser's own scroll restoration cannot
 * reach the scroller; this hook saves `scrollTop` per `location.key` from the
 * returned scroll handler and, in a layout effect on every location change,
 * applies `resolveScrollTarget` before the new pane paints.
 *
 * A pane is often shorter than the target on that first frame — its table
 * mounts a beat after its header — and the browser clamps the assignment, so
 * the restore retries once per frame until the pane is tall enough, the user
 * scrolls, or `RESTORE_DEADLINE_MS` passes. Positions live in memory only; a
 * reload starts at the top.
 *
 * @param scrollerRef The pane scroller, the same ref `useHeaderScrollProgress` attaches.
 * @returns `onScroll` to wire as the scroller's scroll handler alongside the header's.
 */
export const usePaneScrollRestoration = (scrollerRef: React.RefObject<HTMLDivElement | null>) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = React.useRef(new Map<string, number>());
  const locationKey = React.useRef(location.key);
  // A programmatic `scrollTop` assignment fires a scroll event of its own; the
  // handler must not mistake it (possibly clamped by short content) for the user.
  const suppressRecording = React.useRef(false);
  const pending = React.useRef<IPendingRestore | null>(null);
  const unsuppressFrame = React.useRef<number | null>(null);

  const remember = React.useCallback((key: string, scrollTop: number) => {
    const saved = positions.current;
    saved.delete(key);
    saved.set(key, scrollTop);
    if (saved.size > MAX_SAVED_POSITIONS) {
      const oldest = saved.keys().next().value;
      if (oldest !== undefined) {
        saved.delete(oldest);
      }
    }
  }, []);

  const settle = React.useCallback(() => {
    if (pending.current !== null) {
      cancelAnimationFrame(pending.current.frame);
      pending.current = null;
    }
    // The assignment's own scroll event lands before the next frame's callbacks.
    if (unsuppressFrame.current !== null) {
      cancelAnimationFrame(unsuppressFrame.current);
    }
    unsuppressFrame.current = requestAnimationFrame(() => {
      unsuppressFrame.current = null;
      suppressRecording.current = false;
    });
  }, []);

  const restore = React.useCallback(
    (scroller: HTMLDivElement, target: number) => {
      if (unsuppressFrame.current !== null) {
        cancelAnimationFrame(unsuppressFrame.current);
        unsuppressFrame.current = null;
      }
      suppressRecording.current = true;
      scroller.scrollTop = target;
      if (scroller.scrollTop === target) {
        settle();
        return;
      }
      const deadline = Date.now() + RESTORE_DEADLINE_MS;
      const retry = () => {
        scroller.scrollTop = target;
        if (scroller.scrollTop === target || Date.now() >= deadline) {
          settle();
          return;
        }
        pending.current = { target, applied: scroller.scrollTop, deadline, frame: requestAnimationFrame(retry) };
      };
      pending.current = { target, applied: scroller.scrollTop, deadline, frame: requestAnimationFrame(retry) };
    },
    [settle]
  );

  React.useLayoutEffect(() => {
    locationKey.current = location.key;
    const scroller = scrollerRef.current;
    if (scroller === null) {
      return;
    }
    if (pending.current !== null) {
      settle();
    }
    const target = resolveScrollTarget({
      navigationType,
      preserveScroll: hasPreserveScrollState(location.state),
      saved: positions.current.get(location.key),
      current: scroller.scrollTop,
    });
    remember(location.key, target);
    if (scroller.scrollTop !== target) {
      restore(scroller, target);
    }
  }, [location.key, location.state, navigationType, remember, restore, scrollerRef, settle]);

  React.useEffect(
    () => () => {
      if (pending.current !== null) {
        cancelAnimationFrame(pending.current.frame);
        pending.current = null;
      }
      if (unsuppressFrame.current !== null) {
        cancelAnimationFrame(unsuppressFrame.current);
        unsuppressFrame.current = null;
      }
    },
    []
  );

  const onScroll = React.useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller === null) {
      return;
    }
    if (pending.current !== null) {
      // A position other than the clamped one is the user taking over.
      if (scroller.scrollTop !== pending.current.applied) {
        settle();
        suppressRecording.current = false;
        remember(locationKey.current, scroller.scrollTop);
      }
      return;
    }
    if (!suppressRecording.current) {
      remember(locationKey.current, scroller.scrollTop);
    }
  }, [remember, scrollerRef, settle]);

  return { onScroll };
};
